import React, { useState, useRef, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Avatar,
  CircularProgress,
  IconButton,
  useTheme,
  useMediaQuery,
  Divider
} from '@mui/material';
import {
  Close as CloseIcon,
  Send as SendIcon,
  AutoAwesome as AgentIcon,
  Person as UserIcon
} from '@mui/icons-material';
import { agentApi } from '../../utils/agentApi';
import { Agent } from '../../types/agent';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

interface AgentCreationChatDialogProps {
  open: boolean;
  onClose: () => void;
  initialDescription: string;
  onAgentCreated: () => void;
}

const AgentCreationChatDialog: React.FC<AgentCreationChatDialogProps> = ({
  open,
  onClose,
  initialDescription,
  onAgentCreated
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user } = useAuth();
  const navigate = useNavigate();
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const [messages, setMessages] = useState<Message[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isCreatingAgent, setIsCreatingAgent] = useState(false);
  const [conversationComplete, setConversationComplete] = useState(false);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (open && initialDescription) {
      // Initialize conversation with AI greeting
      const initialMessages: Message[] = [
        {
          id: '1',
          role: 'assistant',
          content: `Olá! Vou te ajudar a criar um agente personalizado. Vi que você quer: "${initialDescription}". Vamos conversar para entender melhor suas necessidades e criar o agente perfeito para você.`,
          timestamp: new Date()
        },
        {
          id: '2',
          role: 'assistant',
          content: 'Para começar, me conte mais detalhes sobre o tipo de negócio que você tem e quais são os principais objetivos do seu agente.',
          timestamp: new Date()
        }
      ];
      setMessages(initialMessages);
      setConversationComplete(false);
    }
  }, [open, initialDescription]);

  const handleSendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: inputMessage.trim(),
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);

    try {
      // Send message to AI for conversation
      const API_BASE_URL = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
      const response = await fetch(`${API_BASE_URL}/agent/ChatCreation`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify({
          messages: [...messages, userMessage].map(m => ({
            role: m.role,
            content: m.content
          })),
          initialDescription
        })
      });

      if (!response.ok) {
        throw new Error('Failed to get AI response');
      }

      const data = await response.json();

      const assistantMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: data.message,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, assistantMessage]);

      // Check if conversation is complete
      if (data.conversationComplete) {
        setConversationComplete(true);
      }

    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Erro ao enviar mensagem. Tente novamente.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateAgent = async () => {
    if (!conversationComplete) return;

    setIsCreatingAgent(true);

    try {
      // Send conversation to create agent
      const API_BASE_URL = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
      const response = await fetch(`${API_BASE_URL}/agent/CreateFromConversation`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify({
          messages: messages.map(m => ({
            role: m.role,
            content: m.content
          })),
          initialDescription,
          userId: user?.id
        })
      });

      if (!response.ok) {
        throw new Error('Failed to create agent');
      }

      const agent: Agent = await response.json();

      toast.success('Agente criado com sucesso!');
      onAgentCreated();
      navigate(`/admin/agents/${agent.id}`);

    } catch (error) {
      console.error('Error creating agent:', error);
      toast.error('Erro ao criar agente. Tente novamente.');
    } finally {
      setIsCreatingAgent(false);
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          height: isMobile ? '100vh' : '80vh',
          maxHeight: '80vh',
          borderRadius: isMobile ? 0 : 2
        }
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 1,
          borderBottom: 1,
          borderColor: 'divider'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AgentIcon color="primary" />
          <Typography variant="h6" component="div">
            Criação de Agente IA
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
        <Box
          sx={{
            flex: 1,
            overflow: 'auto',
            p: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: 2
          }}
        >
          {messages.map((message) => (
            <Box
              key={message.id}
              sx={{
                display: 'flex',
                justifyContent: message.role === 'user' ? 'flex-end' : 'flex-start',
                mb: 1
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1,
                  maxWidth: '80%',
                  flexDirection: message.role === 'user' ? 'row-reverse' : 'row'
                }}
              >
                <Avatar
                  sx={{
                    bgcolor: message.role === 'user' ? 'primary.main' : 'secondary.main',
                    width: 32,
                    height: 32
                  }}
                >
                  {message.role === 'user' ? <UserIcon /> : <AgentIcon />}
                </Avatar>
                <Paper
                  elevation={1}
                  sx={{
                    p: 2,
                    bgcolor: message.role === 'user'
                      ? 'primary.main'
                      : theme.palette.mode === 'dark'
                        ? 'rgba(255,255,255,0.05)'
                        : 'grey.100',
                    color: message.role === 'user'
                      ? theme.palette.mode === 'dark' ? '#000' : undefined
                      : 'text.primary',
                    borderRadius: 2,
                    maxWidth: '100%',
                    border: message.role === 'assistant' && theme.palette.mode === 'dark'
                      ? '1px solid rgba(255,255,255,0.1)'
                      : 'none'
                  }}
                >
                  <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                    {message.content}
                  </Typography>
                </Paper>
              </Box>
            </Box>
          ))}

          {isLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Avatar sx={{ bgcolor: 'secondary.main', width: 32, height: 32 }}>
                  <AgentIcon />
                </Avatar>
                <Paper
                  elevation={1}
                  sx={{
                    p: 2,
                    bgcolor: theme.palette.mode === 'dark'
                      ? 'rgba(255,255,255,0.05)'
                      : 'grey.100',
                    borderRadius: 2,
                    border: theme.palette.mode === 'dark'
                      ? '1px solid rgba(255,255,255,0.1)'
                      : 'none'
                  }}
                >
                  <CircularProgress size={20} />
                </Paper>
              </Box>
            </Box>
          )}

          <div ref={messagesEndRef} />
        </Box>

        {conversationComplete && (
          <Box sx={{
            p: 2,
            bgcolor: theme.palette.mode === 'dark'
              ? 'rgba(74, 222, 128, 0.15)'
              : 'success.light',
            borderRadius: 1,
            m: 2,
            border: theme.palette.mode === 'dark'
              ? '1px solid rgba(74, 222, 128, 0.3)'
              : 'none'
          }}>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 'bold',
                color: theme.palette.mode === 'dark'
                  ? 'success.light'
                  : 'success.contrastText'
              }}
            >
              ✓ Conversa concluída! Agora você pode criar seu agente personalizado.
            </Typography>
          </Box>
        )}

        <Divider />

        <Box sx={{ p: 2 }}>
          <TextField
            fullWidth
            multiline
            maxRows={3}
            placeholder="Digite sua mensagem..."
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            disabled={isLoading || conversationComplete}
            InputProps={{
              endAdornment: (
                <IconButton
                  onClick={handleSendMessage}
                  disabled={!inputMessage.trim() || isLoading || conversationComplete}
                  color="primary"
                >
                  <SendIcon />
                </IconButton>
              )
            }}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
        <Button onClick={onClose} disabled={isCreatingAgent}>
          Cancelar
        </Button>
        <Button
          onClick={handleCreateAgent}
          variant="contained"
          sx={{ color: theme.palette.mode === 'dark' ? '#000' : undefined }}
          disabled={!conversationComplete || isCreatingAgent}
          startIcon={isCreatingAgent ? <CircularProgress size={16} /> : <AgentIcon />}
        >
          {isCreatingAgent ? 'Criando Agente...' : 'Criar Agente'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AgentCreationChatDialog;
